import Snackbar from '@mui/material/Snackbar';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  GridRowModesModel,
  GridRowModes,
  GridRowParams,
  GridSelectionModel,
  GridRowId,
  GridFilterModel,
  GridSortModel,
  getGridStringOperators,
  GridActionsCellItem,
  GridColumns,
} from '@mui/x-data-grid-pro';
import Alert, { AlertProps } from '@mui/material/Alert';
import { CustomDataTable } from 'components/DatatableComponent';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import { PaginationAndSortingParams } from 'types/api/Common/PaginationTypes';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import { getErrorMessage } from 'utils/Error';
import { DASHBOARD_VIEW_MODE } from 'constant/DashboardTypeConstant';
import {
  useGetTimePeriodSetList,
  useUpsertTimePeriod,
} from 'services/v1/SystemTenant/AROCube/CubeTimePeriodSetService';
import {
  CubeTimePeriodSetRowItem,
  RequestCreateTimePeriodSet,
} from 'types/api/SystemTenant/AROCube/CubeTimePeriodSetTypes';
import { renderSelectEditInputCell } from 'pages/SystemTenant/AROKMS/TableDefinition/components/SelectEditInputCell';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';

const panelStyle = {
  transform: 'translate3d(704px, 140.667px, 0px) !important',

  '& .MuiDataGrid-panelFooter': {
    display: 'none',
  },
};

function isAllFieldEmpty(data: CubeTimePeriodSetRowItem) {
  return (
    data.timePeriodSetName === undefined &&
    data.timePeriodSetName === null &&
    data.timePeriodSetName === '' &&
    data.timePeriod === undefined &&
    data.timePeriod === null &&
    data.timePeriod === '' &&
    data.futureYears === undefined &&
    data.futureYears === null &&
    data.futureYears === '' &&
    data.pastYears === undefined &&
    data.pastYears === null &&
    data.pastYears === ''
  );
}

function checkIsAnyMandatoryFieldEmpty(data: CubeTimePeriodSetRowItem) {
  return (
    data.timePeriodSetName === undefined ||
    data.timePeriodSetName === null ||
    data.timePeriodSetName === '' ||
    data.timePeriod === undefined ||
    data.timePeriod === null ||
    data.timePeriod.value === '' ||
    data.futureYears === undefined ||
    data.futureYears === null ||
    data.pastYears === undefined ||
    data.pastYears === null
  );
}

export default function CubeLayerSetListPage() {
  const { dashboardViewMode } = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<CubeTimePeriodSetRowItem[]>([]);

  const [selectedDataID, setSelectedDataID] = useState<GridRowId[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [filter, setFilter] = useState<PaginationAndSortingParams>({
    page: 0,
    size: 20,
    filterValue: '',
    sortType: 'asc',
    filterOperator: 'contains',
  });

  const [filterButtonElement, setFilterButtonElement] = useState<HTMLButtonElement | null>(null);
  const [editedData, setEditedData] = useState<CubeTimePeriodSetRowItem[]>([]);
  const [snackbar, setSnackbar] = useState<Pick<AlertProps, 'children' | 'severity'> | null>(null);

  const { data: timePeriodSetList, isLoading, refetch: refreshCubeSetLayerData } = useGetTimePeriodSetList();
  const { mutateAsync: upsertTimePeriodSet } = useUpsertTimePeriod();

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const isAnyEdit = useMemo(() => {
    return rowModesModel && Object.values(rowModesModel).some((item) => item.mode === GridRowModes.Edit);
  }, [rowModesModel]);

  const isBuilderViewOnly = dashboardViewMode === DASHBOARD_VIEW_MODE.VIEW_ONLY;

  const columns = useMemo<GridColumns>(() => {
    const cols: GridColumns = [
      {
        field: 'code',
        headerName: 'Code',
        width: 150,
        editable: false,
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'timePeriodSetName',
        headerName: 'Period Set Name',
        width: 200,
        editable: true,
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'timePeriod',
        headerName: 'Time Period',
        width: 200,
        editable: true,
        valueGetter: (params) => {
          if (typeof params.row.timePeriod === 'object') return params.row.timePeriod;

          return timePeriodSetList?.data?.options.timePeriodDropdownOptions.find(
            (item) => item.value === params.row.timePeriod
          );
        },
        valueFormatter: (params) => {
          return params.value?.label;
        },
        renderEditCell: (params) => {
          return renderSelectEditInputCell({
            ...params,
            // @ts-ignore
            optionsSource: timePeriodSetList?.data.options.timePeriodDropdownOptions,
          });
        },
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'pastYears',
        headerName: 'Past Years',
        width: 150,
        editable: true,
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'futureYears',
        headerName: 'Future Years',
        width: 150,
        editable: true,
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
    ];

    if (isBuilderViewOnly) return cols;

    return [
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Edit',
        width: 30,
        cellClassName: 'actions',

        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

          if (isInEditMode) {
            // @ts-ignore
            return [<GridActionsCellItem icon={<SaveIcon />} label='Save' onClick={handleSaveClick(id)} />];
          }

          return [
            // @ts-ignore
            <GridActionsCellItem disabled={isAnyEdit} icon={<EditIcon />} label='Edit' onClick={handleEditClick(id)} />,
          ];
        },
      },
      ...cols,
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timePeriodSetList?.data, rowModesModel, isAnyEdit, isBuilderViewOnly]);

  const DataMutationSuccessHandlerOption = {
    onSuccess: () => {
      refreshCubeSetLayerData();
    },
    onError: (error: AxiosDefaultErrorEntity) => {
      refreshCubeSetLayerData();
      setSnackbar({ children: getErrorMessage(error), severity: 'error' });
    },
  };

  useEffect(() => {
    if (timePeriodSetList?.data) {
      setRowData(timePeriodSetList?.data.data);
      setRowModesModel(
        timePeriodSetList?.data?.data?.reduce((acc, item) => {
          if (item.id) return { ...acc, [item.id]: GridRowModes.View };
          return acc;
        }, {})
      );
    }
  }, [timePeriodSetList]);

  const handleChangePage = (newPage: number) => {
    setFilter({ ...filter, page: newPage });
  };
  const handleChangePageSize = (newPageSize: number) => {
    setFilter({ ...filter, size: newPageSize });
  };

  const handleOnFilter = (params: GridFilterModel) => {
    let filterObj = {};
    if (params.items.length) {
      filterObj = {
        filterValue: params.items[0].value,
        filterOperator: params.items[0].operatorValue,
      };
    } else {
      filterObj = {
        filterValue: '',
        filterOperator: 'contains',
      };
    }
    setFilter({
      ...filter,
      ...filterObj,
    });
  };

  const handleOnSortModelChange = (model: GridSortModel) => {
    if (model.length === 0) {
      setFilter({ ...filter, sortType: 'asc' });
      return;
    }
    setFilter({ ...filter, sortType: model[0].sort });
  };

  // Process row Update will be triggered when
  // user modify or insert new row the table
  const handleProcessRowUpdate = (data: CubeTimePeriodSetRowItem, oldData: CubeTimePeriodSetRowItem) => {
    const isEmpty = isAllFieldEmpty(data);
    const isAnyFieldEmpty = checkIsAnyMandatoryFieldEmpty(data);
    if (isAnyFieldEmpty) return;
    if (isEmpty && data?.id) handleCancelInsert(data?.id);
    if (data.isNew) {
      const insertPayload: RequestCreateTimePeriodSet = {
        id: null,
        futureYears: data.futureYears,
        pastYears: data.pastYears,
        timePeriodSetName: data.timePeriodSetName,
        timePeriod: data.timePeriod.value,
      };
      // if the row is new and not empy, then insert the row to database
      upsertTimePeriodSet(insertPayload, DataMutationSuccessHandlerOption);
      return { ...data, isNew: false };
    }
    // if the row is not new, it means that the row n
    if (!data.isNew) {
      // if data already exist in state editedData, update it
      // if not, add it to editedData
      const index = editedData.findIndex((item) => item.id === data.id);
      if (index === -1) {
        setEditedData([...editedData, data]);
      } else {
        const newEditedData = [...editedData];
        newEditedData[index] = data;
        setEditedData(newEditedData);
      }
      const updatePayload: RequestCreateTimePeriodSet = {
        id: data.id,
        futureYears: data.futureYears,
        pastYears: data.pastYears,
        timePeriodSetName: data.timePeriodSetName,
        timePeriod: data.timePeriod.value,
      };
      upsertTimePeriodSet(updatePayload, DataMutationSuccessHandlerOption);
      return data;
    }
  };

  const handleCancelInsert = (id: string | number) => {
    const newRows = rowData.filter((item) => item.id !== id);
    setRowData(newRows);
    setRowModesModel((prev) => {
      const newModel = { ...prev };
      delete newModel[id];
      return newModel;
    });
  };

  const handleEditRowModes = (params: GridRowParams) => {
    if (isBuilderViewOnly || isAnyEdit) return;
    setRowModesModel({ ...rowModesModel, [params.id]: { mode: GridRowModes.Edit } });
  };

  const handleCloseSnackbar = () => setSnackbar(null);

  return (
    <MainLayoutComponent
      pageTitle='Time Period Set'
      breadcrumbs={[t('SIDEBAR_MENU.DASHBOARD'), t('SIDEBAR_MENU.DATA_BUILDER')]}
    >
      <CustomDataTable
        initialState={{
          pagination: {
            page: 0,
          },
          sorting: {
            sortModel: [
              {
                field: 'code',
                sort: 'asc',
              },
            ],
          },
        }}
        loading={isLoading}
        checkboxSelection={!isBuilderViewOnly}
        rows={rowData}
        columns={columns}
        rowCount={timePeriodSetList?.data?.data.length || 0}
        page={filter.page || 0}
        pageSize={filter.size}
        getRowId={(row) => row.id}
        rowModesModel={rowModesModel}
        disableSelectionOnClick
        rowsPerPageOptions={[5, 10, 20]}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        experimentalFeatures={{ newEditingApi: true }}
        onFilterModelChange={handleOnFilter}
        onSortModelChange={handleOnSortModelChange}
        onPageChange={handleChangePage}
        onPageSizeChange={handleChangePageSize}
        onSelectionModelChange={(selectedId: GridSelectionModel) => {
          setSelectedDataID(selectedId);
        }}
        processRowUpdate={handleProcessRowUpdate}
        onRowDoubleClick={handleEditRowModes}
        paginationMode='client'
        sortingMode='client'
        filterMode='client'
        componentsProps={{
          panel: {
            sx: panelStyle,
            anchorEl: filterButtonElement,
          },
          toolbar: {
            setRows: setRowData,
            setRowModesModel,
            setFilterButtonElement: setFilterButtonElement,
            disabled: isLoading || isBuilderViewOnly,
            disabledDeleteButton: selectedDataID.length === 0,
            isEditing: rowData.some((item) => item.isNew),
            config: {
              deleteButtonText: 'Delete',
              fieldFocusOnInsert: 'timePeriodSetName',
              insertButtonText: 'Add New Time Period Set',
              insertInitialObject: {
                timePeriodSetName: '',
                timePeriod: '',
                futureYears: 0,
                pastYears: 0,
                isNew: true,
              },
            },
          },
        }}
      />

      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={handleCloseSnackbar}
          autoHideDuration={3000}
        >
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </MainLayoutComponent>
  );
}
